import { Injectable } from '@angular/core';
import { holdsActiveCustomModel } from '../../../../functions/client-model/client-node/holds-active-custom-model';
import { holdsInitialisedUtilisationTE } from '../../../../functions/client-model/client-node/holds-initialised-utilisation-te';
import { getBoardYearsFromTimeline } from '../../../../functions/timeline/get-board-years-from-timeline';
import { BoardTimeline } from '../../../../model/board-timeline';
import { ClientNode } from '../../../../model/client-model/client-node';
import { Dict } from '../../../../model/dict';
import { ClientBoardContainer } from '../../../../public/dashboard/shared/model/client-board-container';
import { DashboardV4CalculationResult } from '../dashboard-calculation/dashboard-v4-calculation-result';
import { TransitionItem } from './transition-item';

export type TransitionResultRecord = {
  teTransitions: Dict<{ target?: number; outcome?: number; carbonAbatement?: number }>;
  teCarbonAbatement: Dict<{ fullTransition: number; onlyUtilisation: number }>;
};
type CollectedTransitionData = Dict<{ [year: number]: TransitionItem }>;

@Injectable({
  providedIn: 'root',
})
export class TransitionDataService {
  create(board: ClientBoardContainer, result: DashboardV4CalculationResult[]): CollectedTransitionData {
    const { timeline } = board.boardSettings;
    const extendedResult = this.buildExtendedResult(result, timeline);
    const allYears = getBoardYearsFromTimeline(timeline);

    return this.collectTransitionData(board.nodes, allYears, extendedResult);
  }

  private buildExtendedResult(
    result: DashboardV4CalculationResult[],
    timeline: BoardTimeline
  ): TransitionResultRecord[] {
    const transitionResult: TransitionResultRecord[] = result.map(({ teTransitions, teCarbonAbatement }) => ({
      teTransitions,
      teCarbonAbatement,
    }));

    const extendedResult = new Array(timeline.scenarioStartYear - timeline.boardStartYear)
      .fill({ teTransitions: {}, teCarbonAbatement: {} })
      .concat(transitionResult);

    return extendedResult;
  }

  private collectTransitionData(
    nodes: Dict<ClientNode>,
    allYears: number[],
    extendedResult: TransitionResultRecord[]
  ): CollectedTransitionData {
    return Object.entries(nodes).reduce((acc, [nodeId, node]) => {
      const nodeTransitionData = {} as Record<number, TransitionItem>;

      allYears.forEach((year, ix) => {
        if (holdsInitialisedUtilisationTE(node)) {
          let teId = node.nodeProperties?.transitionTargetData?.transitionTargetProperties?.transitionElement;
          const teTransitionData = extendedResult[ix]?.teTransitions[teId!] ?? undefined;

          nodeTransitionData[year] = {
            target: teTransitionData?.target ?? undefined,
            outcome: node.nodeProperties.transitionTargetData?.transitionOutcome.outcome[year],
            carbonAbatement: extendedResult[ix]?.teCarbonAbatement[teId!]?.fullTransition ?? undefined,
          } as TransitionItem;

          acc[nodeId] = nodeTransitionData;
        } else if (holdsActiveCustomModel(node)) {
          nodeTransitionData[year] = {
            target: node.nodeProperties.customTargetModelData?.chartData.target[year],
            outcome: node.nodeProperties.customTargetModelData?.chartData.outcome[year],

            //This array is ensured not to be sparse, see MOD-1321 for future handling of this.
            carbonAbatement: node.nodeProperties.customTargetModelData?.chartData.carbonAbatement[year],
          } as TransitionItem;

          acc[nodeId] = nodeTransitionData;
        }
      });

      return acc;
    }, {} as CollectedTransitionData);
  }
}
